import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import ImageUpload from '../ui/ImageUpload';

interface FormStyles {
  backgroundType: 'color' | 'gradient' | 'image' | 'pattern';
  backgroundColor: string;
  backgroundGradient: { from: string; to: string; direction: string } | null;
  backgroundImageUrl: string | null;
  backgroundPattern: string | null;
  backgroundOpacity: number;
  fontFamily: string;
  fontSizeBase: number;
  headingFont: string | null;
  headerImageUrl: string | null;
  headerImagePosition: 'left' | 'center' | 'right' | 'cover';
  formIconUrl: string | null;
  formIconEmoji: string | null;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  accentColor: string;
  errorColor: string;
  successColor: string;
  formWidth: 'narrow' | 'medium' | 'wide' | 'full';
  formPadding: 'compact' | 'normal' | 'relaxed';
  borderRadius: 'none' | 'small' | 'medium' | 'large' | 'full';
  shadow: 'none' | 'small' | 'medium' | 'large';
  customCss: string | null;
}

interface FormStylingProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter', category: 'Sans-serif' },
  { value: 'Roboto', label: 'Roboto', category: 'Sans-serif' },
  { value: 'Open Sans', label: 'Open Sans', category: 'Sans-serif' },
  { value: 'Lato', label: 'Lato', category: 'Sans-serif' },
  { value: 'Poppins', label: 'Poppins', category: 'Sans-serif' },
  { value: 'Montserrat', label: 'Montserrat', category: 'Sans-serif' },
  { value: 'Nunito', label: 'Nunito', category: 'Sans-serif' },
  { value: 'Merriweather', label: 'Merriweather', category: 'Serif' },
  { value: 'Playfair Display', label: 'Playfair Display', category: 'Serif' },
  { value: 'Lora', label: 'Lora', category: 'Serif' },
  { value: 'PT Serif', label: 'PT Serif', category: 'Serif' },
  { value: 'JetBrains Mono', label: 'JetBrains Mono', category: 'Monospace' },
  { value: 'Fira Code', label: 'Fira Code', category: 'Monospace' },
  { value: 'Oswald', label: 'Oswald', category: 'Display' },
  { value: 'Raleway', label: 'Raleway', category: 'Display' },
];

const GRADIENT_DIRECTIONS = [
  { value: 'to-t', label: 'To Top' },
  { value: 'to-b', label: 'To Bottom' },
  { value: 'to-l', label: 'To Left' },
  { value: 'to-r', label: 'To Right' },
  { value: 'to-tr', label: 'To Top Right' },
  { value: 'to-bl', label: 'To Bottom Left' },
];

const PATTERNS = [
  { value: 'dots', label: 'Dots' },
  { value: 'grid', label: 'Grid' },
  { value: 'lines', label: 'Lines' },
  { value: 'waves', label: 'Waves' },
  { value: 'zigzag', label: 'Zigzag' },
];

const COMMON_EMOJIS = ['📋', '📝', '✅', '📊', '🎯', '💼', '🎨', '⭐', '💡', '🚀', '📌', '🔔'];

const DEFAULT_STYLES: FormStyles = {
  backgroundType: 'color',
  backgroundColor: '#ffffff',
  backgroundGradient: null,
  backgroundImageUrl: null,
  backgroundPattern: null,
  backgroundOpacity: 100,
  fontFamily: 'Inter',
  fontSizeBase: 16,
  headingFont: null,
  headerImageUrl: null,
  headerImagePosition: 'center',
  formIconUrl: null,
  formIconEmoji: null,
  logoUrl: null,
  primaryColor: '#3b82f6',
  secondaryColor: '#6366f1',
  textColor: '#1f2937',
  accentColor: '#8b5cf6',
  errorColor: '#ef4444',
  successColor: '#10b981',
  formWidth: 'medium',
  formPadding: 'normal',
  borderRadius: 'medium',
  shadow: 'medium',
  customCss: null,
};

export default function FormStyling({ formId, isOpen, onClose }: FormStylingProps) {
  const [styles, setStyles] = useState<FormStyles>(DEFAULT_STYLES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'background' | 'typography' | 'branding' | 'colors' | 'layout' | 'advanced'>('background');

  useEffect(() => {
    if (isOpen && formId) {
      loadStyles();
    }
  }, [isOpen, formId]);

  async function loadStyles() {
    try {
      setLoading(true);
      const response = await api.get(`/api/forms/${formId}/styles`);
      if (response.ok) {
        const data = await response.json() as { styles: FormStyles };
        setStyles({ ...DEFAULT_STYLES, ...data.styles });
      }
    } catch (error) {
      console.error('Failed to load styles:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      const response = await api.put(`/api/forms/${formId}/styles`, styles);
      if (response.ok) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to save styles:', error);
    } finally {
      setSaving(false);
    }
  }

  function updateStyles(updates: Partial<FormStyles>) {
    setStyles((prev) => ({ ...prev, ...updates }));
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Form Styling</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Sidebar Tabs */}
            <div className="w-48 bg-gray-50 border-r p-4 space-y-1">
              {[
                { id: 'background', label: 'Background', icon: '🎨' },
                { id: 'typography', label: 'Typography', icon: '🔤' },
                { id: 'branding', label: 'Branding', icon: '🏷️' },
                { id: 'colors', label: 'Colors', icon: '🌈' },
                { id: 'layout', label: 'Layout', icon: '📐' },
                { id: 'advanced', label: 'Advanced', icon: '⚙️' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content Area */}
            <div className="flex-1 p-6 overflow-y-auto">
              {activeTab === 'background' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Background Type</label>
                    <div className="grid grid-cols-4 gap-2">
                      {['color', 'gradient', 'image', 'pattern'].map((type) => (
                        <button
                          key={type}
                          onClick={() => updateStyles({ backgroundType: type as FormStyles['backgroundType'] })}
                          className={`px-3 py-2 rounded-lg border text-sm capitalize ${
                            styles.backgroundType === type
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {styles.backgroundType === 'color' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={styles.backgroundColor}
                          onChange={(e) => updateStyles({ backgroundColor: e.target.value })}
                          className="w-12 h-10 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={styles.backgroundColor}
                          onChange={(e) => updateStyles({ backgroundColor: e.target.value })}
                          className="input w-32"
                        />
                      </div>
                    </div>
                  )}

                  {styles.backgroundType === 'gradient' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">From Color</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={styles.backgroundGradient?.from || '#3b82f6'}
                              onChange={(e) =>
                                updateStyles({
                                  backgroundGradient: {
                                    ...styles.backgroundGradient!,
                                    from: e.target.value,
                                    to: styles.backgroundGradient?.to || '#8b5cf6',
                                    direction: styles.backgroundGradient?.direction || 'to-b',
                                  },
                                })
                              }
                              className="w-10 h-8 rounded cursor-pointer"
                            />
                            <input
                              type="text"
                              value={styles.backgroundGradient?.from || '#3b82f6'}
                              onChange={(e) =>
                                updateStyles({
                                  backgroundGradient: {
                                    ...styles.backgroundGradient!,
                                    from: e.target.value,
                                    to: styles.backgroundGradient?.to || '#8b5cf6',
                                    direction: styles.backgroundGradient?.direction || 'to-b',
                                  },
                                })
                              }
                              className="input flex-1"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">To Color</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={styles.backgroundGradient?.to || '#8b5cf6'}
                              onChange={(e) =>
                                updateStyles({
                                  backgroundGradient: {
                                    ...styles.backgroundGradient!,
                                    from: styles.backgroundGradient?.from || '#3b82f6',
                                    to: e.target.value,
                                    direction: styles.backgroundGradient?.direction || 'to-b',
                                  },
                                })
                              }
                              className="w-10 h-8 rounded cursor-pointer"
                            />
                            <input
                              type="text"
                              value={styles.backgroundGradient?.to || '#8b5cf6'}
                              onChange={(e) =>
                                updateStyles({
                                  backgroundGradient: {
                                    ...styles.backgroundGradient!,
                                    from: styles.backgroundGradient?.from || '#3b82f6',
                                    to: e.target.value,
                                    direction: styles.backgroundGradient?.direction || 'to-b',
                                  },
                                })
                              }
                              className="input flex-1"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Direction</label>
                        <select
                          value={styles.backgroundGradient?.direction || 'to-b'}
                          onChange={(e) =>
                            updateStyles({
                              backgroundGradient: {
                                ...styles.backgroundGradient!,
                                from: styles.backgroundGradient?.from || '#3b82f6',
                                to: styles.backgroundGradient?.to || '#8b5cf6',
                                direction: e.target.value,
                              },
                            })
                          }
                          className="input"
                        >
                          {GRADIENT_DIRECTIONS.map((dir) => (
                            <option key={dir.value} value={dir.value}>
                              {dir.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* Preview */}
                      <div
                        className="h-24 rounded-lg border"
                        style={{
                          background: `linear-gradient(${
                            styles.backgroundGradient?.direction === 'to-t' ? 'to top' :
                            styles.backgroundGradient?.direction === 'to-b' ? 'to bottom' :
                            styles.backgroundGradient?.direction === 'to-l' ? 'to left' :
                            styles.backgroundGradient?.direction === 'to-r' ? 'to right' :
                            styles.backgroundGradient?.direction === 'to-tr' ? 'to top right' :
                            'to bottom left'
                          }, ${styles.backgroundGradient?.from || '#3b82f6'}, ${styles.backgroundGradient?.to || '#8b5cf6'})`,
                        }}
                      ></div>
                    </div>
                  )}

                  {styles.backgroundType === 'image' && (
                    <ImageUpload
                      value={styles.backgroundImageUrl}
                      onChange={(url) => updateStyles({ backgroundImageUrl: url })}
                      formId={formId}
                      fieldId="background-image"
                      label="Background Image"
                      maxSizeMB={10}
                      placeholder="https://example.com/image.jpg"
                    />
                  )}

                  {styles.backgroundType === 'pattern' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pattern</label>
                      <div className="grid grid-cols-5 gap-2">
                        {PATTERNS.map((pattern) => (
                          <button
                            key={pattern.value}
                            onClick={() => updateStyles({ backgroundPattern: pattern.value })}
                            className={`px-3 py-2 rounded-lg border text-sm ${
                              styles.backgroundPattern === pattern.value
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {pattern.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Opacity: {styles.backgroundOpacity}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={styles.backgroundOpacity}
                      onChange={(e) => updateStyles({ backgroundOpacity: parseInt(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'typography' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                    <select
                      value={styles.fontFamily}
                      onChange={(e) => updateStyles({ fontFamily: e.target.value })}
                      className="input"
                    >
                      {Object.entries(
                        FONT_OPTIONS.reduce((acc, font) => {
                          acc[font.category] = acc[font.category] || [];
                          acc[font.category].push(font);
                          return acc;
                        }, {} as Record<string, typeof FONT_OPTIONS>)
                      ).map(([category, fonts]) => (
                        <optgroup key={category} label={category}>
                          {fonts.map((font) => (
                            <option key={font.value} value={font.value}>
                              {font.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Base Font Size: {styles.fontSizeBase}px
                    </label>
                    <input
                      type="range"
                      min="12"
                      max="24"
                      value={styles.fontSizeBase}
                      onChange={(e) => updateStyles({ fontSizeBase: parseInt(e.target.value) })}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Heading Font (optional)</label>
                    <select
                      value={styles.headingFont || ''}
                      onChange={(e) => updateStyles({ headingFont: e.target.value || null })}
                      className="input"
                    >
                      <option value="">Same as body font</option>
                      {FONT_OPTIONS.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Preview */}
                  <div className="p-4 border rounded-lg" style={{ fontFamily: styles.fontFamily, fontSize: styles.fontSizeBase }}>
                    <h3 className="text-xl font-bold mb-2" style={{ fontFamily: styles.headingFont || styles.fontFamily }}>
                      Preview Heading
                    </h3>
                    <p>This is how your form text will look with the selected typography settings.</p>
                  </div>
                </div>
              )}

              {activeTab === 'branding' && (
                <div className="space-y-6">
                  <ImageUpload
                    value={styles.headerImageUrl}
                    onChange={(url) => updateStyles({ headerImageUrl: url })}
                    formId={formId}
                    fieldId="header-image"
                    label="Header Image"
                    maxSizeMB={5}
                    placeholder="https://example.com/header.jpg"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Header Image Position</label>
                    <div className="grid grid-cols-4 gap-2">
                      {['left', 'center', 'right', 'cover'].map((pos) => (
                        <button
                          key={pos}
                          onClick={() => updateStyles({ headerImagePosition: pos as FormStyles['headerImagePosition'] })}
                          className={`px-3 py-2 rounded-lg border text-sm capitalize ${
                            styles.headerImagePosition === pos
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {pos}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Form Icon</label>
                    <div className="flex gap-4 items-start">
                      <div className="flex-1">
                        <ImageUpload
                          value={styles.formIconUrl}
                          onChange={(url) => updateStyles({ formIconUrl: url, formIconEmoji: null })}
                          formId={formId}
                          fieldId="form-icon"
                          maxSizeMB={1}
                          placeholder="https://example.com/icon.png"
                          showUrlInput={true}
                        />
                      </div>
                      <div className="text-center text-gray-400 pt-12">or</div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Emoji</label>
                        <div className="flex flex-wrap gap-1 max-w-[144px]">
                          {COMMON_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              onClick={() => updateStyles({ formIconEmoji: emoji, formIconUrl: null })}
                              className={`w-8 h-8 text-lg rounded ${
                                styles.formIconEmoji === emoji ? 'bg-primary-100 ring-2 ring-primary-500' : 'hover:bg-gray-100'
                              }`}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <ImageUpload
                    value={styles.logoUrl}
                    onChange={(url) => updateStyles({ logoUrl: url })}
                    formId={formId}
                    fieldId="logo"
                    label="Logo"
                    maxSizeMB={2}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              )}

              {activeTab === 'colors' && (
                <div className="space-y-6">
                  {[
                    { key: 'primaryColor', label: 'Primary Color', desc: 'Buttons, links, and accents' },
                    { key: 'secondaryColor', label: 'Secondary Color', desc: 'Secondary actions and highlights' },
                    { key: 'textColor', label: 'Text Color', desc: 'Main text content' },
                    { key: 'accentColor', label: 'Accent Color', desc: 'Focus states and highlights' },
                    { key: 'successColor', label: 'Success Color', desc: 'Success messages and states' },
                    { key: 'errorColor', label: 'Error Color', desc: 'Error messages and validation' },
                  ].map((color) => (
                    <div key={color.key}>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-sm font-medium text-gray-700">{color.label}</label>
                        <span className="text-xs text-gray-400">{color.desc}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={styles[color.key as keyof FormStyles] as string}
                          onChange={(e) => updateStyles({ [color.key]: e.target.value })}
                          className="w-12 h-10 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={styles[color.key as keyof FormStyles] as string}
                          onChange={(e) => updateStyles({ [color.key]: e.target.value })}
                          className="input w-32"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'layout' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Form Width</label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { value: 'narrow', label: 'Narrow', width: '480px' },
                        { value: 'medium', label: 'Medium', width: '640px' },
                        { value: 'wide', label: 'Wide', width: '800px' },
                        { value: 'full', label: 'Full', width: '100%' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => updateStyles({ formWidth: option.value as FormStyles['formWidth'] })}
                          className={`px-3 py-2 rounded-lg border text-sm ${
                            styles.formWidth === option.value
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-gray-400">{option.width}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Padding</label>
                    <div className="grid grid-cols-3 gap-2">
                      {['compact', 'normal', 'relaxed'].map((padding) => (
                        <button
                          key={padding}
                          onClick={() => updateStyles({ formPadding: padding as FormStyles['formPadding'] })}
                          className={`px-3 py-2 rounded-lg border text-sm capitalize ${
                            styles.formPadding === padding
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {padding}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Border Radius</label>
                    <div className="grid grid-cols-5 gap-2">
                      {['none', 'small', 'medium', 'large', 'full'].map((radius) => (
                        <button
                          key={radius}
                          onClick={() => updateStyles({ borderRadius: radius as FormStyles['borderRadius'] })}
                          className={`px-3 py-2 rounded-lg border text-sm capitalize ${
                            styles.borderRadius === radius
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {radius}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Shadow</label>
                    <div className="grid grid-cols-4 gap-2">
                      {['none', 'small', 'medium', 'large'].map((shadow) => (
                        <button
                          key={shadow}
                          onClick={() => updateStyles({ shadow: shadow as FormStyles['shadow'] })}
                          className={`px-3 py-2 rounded-lg border text-sm capitalize ${
                            styles.shadow === shadow
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {shadow}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'advanced' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Custom CSS</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Add custom CSS to further customize your form. Use .form-container, .form-field, .form-label, etc.
                    </p>
                    <textarea
                      value={styles.customCss || ''}
                      onChange={(e) => updateStyles({ customCss: e.target.value || null })}
                      className="input font-mono text-sm"
                      rows={10}
                      placeholder={`.form-container {\n  /* Your custom styles */\n}`}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
